import React, { useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Avatar,
  Collapse,
  CssBaseline,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Chip,
  Alert,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import {
  ChevronLeft,
  ChevronRight,
  PeopleOutlined,
  AdminPanelSettingsOutlined,
  ManageAccountsOutlined,
  ExpandMore,
  AccountTreeOutlined,
  ExpandLess,
  QuizOutlined,
  QuestionAnswerOutlined,
  Person,
  SettingsOutlined,
  TuneOutlined,
  SupervisorAccountOutlined,
  FactCheckOutlined,
  ArticleOutlined,
  AssignmentOutlined,
  HistoryOutlined,
  ReportProblemOutlined,
  HomeWorkOutlined,
  BarChart,
  AccountBalanceOutlined,
  PaletteOutlined,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import Logo from '../../../assets/images/Voxtur-verify.png';
import { configuration, login, setSelectedOffice } from '../../../store/slices/authSlice';
import { useDispatch, useSelector } from 'react-redux';
import Breadcrumb from '../../common/Breadcrumb';
import HelpFab from '../../common/HelpFab';
import ListItemComponent from '../../common/ListItem';
import Loader from '../../common/Loader';
import ProfileDrawer from '../../common/ProfileDrawer';
import { toggleMenuCollapsed, setDDOpen } from '../../../store/slices/breadcrumbSlice';
import {
  ADMIN_ROLE,
  CLIENT_ADMIN_ROLE,
  CLIENT_USER_ROLE,
  OFFICE_ADMIN_ROLE,
  SUPER_ADMIN_ROLE,
} from '../../../utils/constants';
import chroma from 'chroma-js';
import LogoComponent from './Logo';
import { getConfigurations } from '../../../services/auth/auth';
import { pickOfficeBranding } from '../../../utils/officeBranding';

// Roles that can access this ('admin' type) layout. Restrict a menu entry to
// super admin only by setting roles: [SUPER_ADMIN_ROLE] instead of ALL_ADMIN_ROLES.
const ALL_ADMIN_ROLES = [SUPER_ADMIN_ROLE, CLIENT_ADMIN_ROLE, OFFICE_ADMIN_ROLE];

// Special sentinel for the one entry whose route depends on the logged in role
// (super admin manages all clients, client/office admin manage their own client).
const CLIENTS_AND_OFFICES_ROUTE = 'CLIENTS_AND_OFFICES_ROUTE';

// Dismissal is stored against the specific set of closed offices (and the user,
// since a browser can be shared) rather than a plain boolean, so hiding the
// notice today does not suppress it when a different office closes later.
const CLOSED_OFFICE_NOTICE_KEY = 'closedOfficeNoticeDismissed';

const getClosedOfficeNoticeKey = (userId) => `${CLOSED_OFFICE_NOTICE_KEY}:${userId ?? 'unknown'}`;

const readDismissedClosedOffices = (userId) => {
  try {
    return localStorage.getItem(getClosedOfficeNoticeKey(userId)) || '';
  } catch {
    return '';
  }
};

// Some sidebar routes nest under another (e.g. /admin/templates/master under
// /admin/templates), so the current path can match more than one entry — the
// longest (most specific) match is the one that should show as active.
const getActiveRoute = (routes, pathname) => {
  let activeRoute = null;
  routes.forEach((route) => {
    if (!route) return;
    const isMatch = pathname === route || pathname?.startsWith(`${route}/`);
    if (isMatch && (!activeRoute || route.length > activeRoute.length)) {
      activeRoute = route;
    }
  });
  return activeRoute;
};

const SIDEBAR_MENU_CONFIG = [
  {
    key: 'dashboard',
    type: 'item',
    label: 'Dashboard',
    route: '/admin/dashboard',
    icon: BarChart,
    roles: ALL_ADMIN_ROLES,
  },
  {
    key: 'orders',
    type: 'item',
    label: 'Orders',
    route: '/admin/orders',
    icon: HomeWorkOutlined,
    roles: ALL_ADMIN_ROLES,
  },
  {
    key: 'applicationAdmin',
    type: 'group',
    label: 'Application Admin',
    icon: TuneOutlined,
    ddKey: 'dd1Open',
    roles: [SUPER_ADMIN_ROLE],
    children: [
      {
        key: 'applicationAdmin-orderFormConfiguration',
        label: 'Order Setup',
        route: '/admin/order-types/master',
        icon: AssignmentOutlined,
      },
      {
        key: 'applicationAdmin-masterQuestionnaireConfiguration',
        label: 'Form Setup',
        route: '/admin/templates/master',
        icon: QuizOutlined,
      },
      {
        key: 'applicationAdmin-documentConfiguration',
        label: 'Document Configuration',
        route: '/admin/document-configurations/master',
        icon: ArticleOutlined,
      },
      {
        key: 'applicationAdmin-preferences',
        label: 'Preferences',
        route: '/admin/configurations/master',
        icon: SettingsOutlined,
      },
    ],
  },
  {
    key: 'clientSetup',
    type: 'group',
    label: 'Client Setup',
    icon: SupervisorAccountOutlined,
    ddKey: 'dd2Open',
    roles: ALL_ADMIN_ROLES,
    children: [
      {
        key: 'clientSetup-clientsAndOffices',
        label: 'Clients and Offices',
        route: CLIENTS_AND_OFFICES_ROUTE,
        icon: AccountBalanceOutlined,
      },
      {
        key: 'clientSetup-users',
        label: 'Users',
        route: '/admin/users',
        icon: PeopleOutlined,
      },
      {
        key: 'clientSetup-orderFormConfiguration',
        label: 'Order Setup',
        route: '/admin/order-types',
        icon: AssignmentOutlined,
      },

      {
        key: 'clientSetup-questionnaireConfiguration',
        label: 'Form Setup',
        route: '/admin/templates',
        icon: QuizOutlined,
      },
      {
        key: 'clientSetup-documentConfiguration',
        label: 'Document Configuration',
        route: '/admin/document-configurations',
        icon: ArticleOutlined,
      },
      {
        key: 'clientSetup-preferences',
        label: 'Preferences',
        route: '/admin/configurations',
        icon: SettingsOutlined,
      },
      {
        key: 'clientSetup-branding',
        label: 'Branding and Messaging',
        route: '/admin/branding',
        icon: PaletteOutlined,
      },
      {
        key: 'clientSetup-auditTrails',
        label: 'Audit Trails',
        route: '/admin/audit-trails',
        icon: FactCheckOutlined,
      },
    ],
  },
  {
    key: 'systemAdmin',
    type: 'group',
    label: 'System Admin',
    icon: AdminPanelSettingsOutlined,
    ddKey: 'dd3Open',
    roles: [SUPER_ADMIN_ROLE],
    children: [
      {
        key: 'systemAdmin-adminUsers',
        label: 'Admin Users',
        route: '/admin/list',
        icon: ManageAccountsOutlined,
      },
      {
        key: 'systemAdmin-sourceMapping',
        label: 'Source Mapping',
        route: '/admin/sources',
        icon: AccountTreeOutlined,
      },
      {
        key: 'systemAdmin-integrationLogs',
        label: 'Integration Logs',
        route: '/admin/integration-logs',
        icon: HistoryOutlined,
      },
      {
        key: 'integrationFailureManagement',
        label: 'Integration Failure Management',
        route: '/admin/integration-failure-management',
        icon: ReportProblemOutlined,
      },
    ],
  },

  {
    key: 'helpAndFaqs',
    type: 'item',
    label: 'Help & FAQs',
    route: '/admin/documentation',
    icon: QuestionAnswerOutlined,
    roles: ALL_ADMIN_ROLES,
  },
];

// Defined at module scope (not inside MainLayout) so `Drawer`/`DrawerHeader`
// keep a stable component identity across renders. `styled()` creates a new
// component type on every call — if these lived inside the component body,
// every re-render (e.g. toggling a submenu) would give `<Drawer>` a new type,
// making React unmount/remount the whole sidebar DOM node and reset its
// scroll position.
const drawerWidth = 300;
const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(8)})`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(9)})`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  })
);

const MainLayout = ({ children }) => {
  const url = window.location.origin;
  const location = useLocation();
  const { admin, order } = useSelector((state) => state?.authSlice);
  const isMenuOpen = useSelector((state) => state?.breadcrumbSlice)?.['isMenuOpen'];
  const dd1Open = useSelector((state) => state?.breadcrumbSlice)?.['dd1Open'];
  const dd2Open = useSelector((state) => state?.breadcrumbSlice)?.['dd2Open'];
  const dd3Open = useSelector((state) => state?.breadcrumbSlice)?.['dd3Open'];
  const config = useSelector((state) => state.authSlice)?.configuration?.[url];
  const isSuperAdmin = admin?.user_role_name === SUPER_ADMIN_ROLE ? true : false;
  const isClientAdmin = admin?.user_role_name === CLIENT_ADMIN_ROLE ? true : false;
  const clientLevelAccess =
    admin?.user_role_name === CLIENT_ADMIN_ROLE || admin?.user_role_name === CLIENT_USER_ROLE;
  const isAdmin = admin?.user_role_name === ADMIN_ROLE ? true : false;
  const hasAccess = (roles) => roles?.includes(admin?.user_role_name);
  const ddOpenMap = { dd1Open, dd2Open, dd3Open };
  const resolveRoute = (route) => {
    if (route !== CLIENTS_AND_OFFICES_ROUTE) return route;
    if (isSuperAdmin) return '/admin/clients';
    if (admin?.client_id) return `/admin/clients/${admin.user_id}/${admin.client_id}`;
    return null;
  };
  const activeRoute = getActiveRoute(
    SIDEBAR_MENU_CONFIG.flatMap((section) =>
      section.type === 'item'
        ? [section.route]
        : section.children.map((child) => resolveRoute(child.route))
    ),
    location.pathname
  );
  // Lets a collapsed group still read as "you are in here" instead of only the
  // (currently hidden) child row showing the active state.
  const isGroupActive = (section) =>
    section.children?.some((child) => resolveRoute(child.route) === activeRoute);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [disableOffices, setDisableOffices] = useState([]);
  const [dismissedClosedOffices, setDismissedClosedOffices] = useState(() =>
    readDismissedClosedOffices(admin?.user_id)
  );

  // Sorted so a reordered API response does not read as a different set and
  // resurface a notice the user already dismissed.
  const closedOfficesKey = useMemo(
    () => [...(disableOffices || [])].sort().join('|'),
    [disableOffices]
  );

  const showClosedOfficeNotice = Boolean(
    clientLevelAccess && disableOffices?.length && dismissedClosedOffices !== closedOfficesKey
  );

  const dismissClosedOfficeNotice = () => {
    try {
      localStorage.setItem(getClosedOfficeNoticeKey(admin?.user_id), closedOfficesKey);
    } catch {
      // Private browsing or a full quota - the notice still hides for this session.
    }
    setDismissedClosedOffices(closedOfficesKey);
  };

  useEffect(() => {
    getConfigurationDetails();
  }, []);

  useEffect(() => {
    if (!location.pathname.startsWith('/admin/orders')) {
      dispatch(
        login({
          data: { ...order, lastVisitedOrder: null },
          code: 'order',
        })
      );
    }
  }, [location.pathname]);

  const getConfigurationDetails = async (updateConfig = false, ofc = null) => {
    setLoading(true);
    let body = { domain: url };
    if (ofc) {
      body.office_name = ofc;
    }
    const res = await getConfigurations(body);
    if (res.success) {
      setDisableOffices(
        (res?.response?.filter((ofc) => ofc.status === 'disabled') || [])?.map(
          (ofc) => ofc.office_name
        )
      );
      if (updateConfig) {
        let activeOffices = res?.response?.filter((ofc) => ofc.status !== 'disabled');
        const selected = ofc
          ? activeOffices.find((item) => item.office_name?.replace(/ /g, '') === ofc) ||
            activeOffices?.[0]
          : pickOfficeBranding(activeOffices, admin?.offices);

        dispatch(
          configuration({
            url,
            data: { ...(selected || {}) },
          })
        );
        window.location.reload();
      }
    } else {
      setDisableOffices([]);
    }
    setLoading(false);
  };

  const logoUrl = useMemo(() => {
    // If config is not loaded yet, use local logo to prevent blinking
    if (!config) {
      return Logo;
    }
    // If config is loaded, use API logo if available, otherwise local logo
    return config?.company_logo_url ? config?.company_logo_url : Logo;
  }, [config]);

  useEffect(() => {
    if (window.innerWidth < 768) {
      dispatch(toggleMenuCollapsed({ open: false }));
    }
  }, [window.innerWidth]);

  useEffect(() => {
    // This will preload the image
    const logoImage = new Image();
    logoImage.src = logoUrl;
  }, [logoUrl]);

  useEffect(() => {
    if (Object.keys(config?.branding_json || {})?.length) {
      document.documentElement.style.setProperty(
        '--primary-color',
        config?.branding_json?.primary_color ?? ''
      );
      document.documentElement.style.setProperty(
        '--sidebar-color',
        config?.branding_json?.sidebar_color ?? ''
      );
      document.documentElement.style.setProperty(
        '--sidebar-font-color',
        config?.branding_json?.sidebar_font_color ?? ''
      );
      document.documentElement.style.setProperty(
        '--sidebar-font-darker-color',
        config?.branding_json?.sidebar_font_color
          ? chroma(config?.branding_json?.sidebar_font_color).darken(0.5).hex()
          : ''
      );
      document.documentElement.style.setProperty(
        '--sidebar-lighten-color',
        config?.branding_json?.sidebar_color
          ? chroma(config?.branding_json?.sidebar_color).brighten(0.5).hex()
          : ''
      );
      document.documentElement.style.setProperty(
        '--header-color',
        config?.branding_json?.header_color ?? ''
      );
      document.documentElement.style.setProperty(
        '--header-font-color',
        config?.branding_json?.header_font_color ?? ''
      );
      document.documentElement.style.setProperty(
        '--bg-color',
        config?.branding_json?.background_color ?? ''
      );
    }
  }, [config]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleToggle = () => {
    dispatch(toggleMenuCollapsed({ open: isMenuOpen !== undefined ? !isMenuOpen : false }));
  };

  // const showResetFilters = useMemo(() => {
  //   const queryParams = getQueryParams();
  //   const filterableParams = Object.keys(queryParams).filter((key) => !EXCLUDED_KEYS.includes(key));
  //   return filterableParams.length > 0;
  // }, [getQueryParams]);

  // const handleResetAll = () => {
  //   const queryParams = getQueryParams();
  //   const searchParams = new URLSearchParams();

  //   // Preserve excluded keys while clearing others
  //   Object.entries(queryParams).forEach(([key, value]) => {
  //     if (EXCLUDED_KEYS.includes(key)) {
  //       searchParams.set(key, value);
  //     }
  //   });

  //   navigate({ search: searchParams.toString() }, { replace: true });
  // };

  const changeOffice = (e) => {
    dispatch(setSelectedOffice(e.target.value));
    const ofcName = admin?.offices?.find((ofc) =>
      e.target.value === 'all' ? ofc.is_primary : ofc.client_office_id === e.target.value
    )?.office_name;
    if (ofcName) {
      getConfigurationDetails(true, ofcName?.replace(/ /g, ''));
    } else {
      window.location.reload();
    }
  };

  return (
    <>
      {loading && <Loader />}
      <CssBaseline />
      <div className='main-layout-div'>
        {' '}
        <Drawer
          variant='permanent'
          open={isMenuOpen}
          className='dashboard-sidebar modern-sidebar'
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          style={{
            background: config?.branding_json?.sidebar_color
              ? config?.branding_json?.sidebar_color
              : '#fff',
          }}
        >
          <DrawerHeader>
            {isMenuOpen === true ? (
              <div className='d-flex align-items-center justify-between w-100p'>
                <LogoComponent src={logoUrl} />
                <IconButton
                  onClick={handleToggle}
                  className='toggle-btn'
                  size='small'
                  sx={{
                    ml: 1,
                    color: 'var(--sidebar-font-color)',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: 'var(--sidebar-lighten-color)',
                      color: 'var(--sidebar-font-darker-color)',
                    },
                  }}
                >
                  <ChevronLeft fontSize='small' />
                </IconButton>
              </div>
            ) : (
              <IconButton
                onClick={handleToggle}
                className='toggle-btn-collapsed'
                size='small'
                sx={{
                  color: 'var(--sidebar-font-color)',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'var(--sidebar-lighten-color)',
                    color: 'var(--sidebar-font-darker-color)',
                  },
                }}
              >
                <ChevronRight fontSize='small' />
              </IconButton>
            )}
          </DrawerHeader>
          <Divider />

          <div className={isMenuOpen ? 'list-item-wrapper' : 'list-item-wrapper-collapsed'}>
            {SIDEBAR_MENU_CONFIG.filter((section) => hasAccess(section.roles)).map((section) => {
              if (section.type === 'item') {
                const SectionIcon = section.icon;
                return (
                  <ListItemComponent
                    key={section.key}
                    label={section.label}
                    route={section.route}
                    icon={<SectionIcon fontSize='small' />}
                    isMenuOpen={isMenuOpen}
                    isSelected={section.route === activeRoute}
                  />
                );
              }

              const isOpen = ddOpenMap[section.ddKey];
              const groupActive = isGroupActive(section);
              const GroupIcon = section.icon;
              const chevronStyle = { color: 'var(--sidebar-font-color)' };
              const groupHeader = (
                <ListItem
                  onClick={() => dispatch(setDDOpen({ key: section.ddKey, value: !isOpen }))}
                  style={{ cursor: 'pointer' }}
                  className={`${groupActive ? 'selected-item' : ''} sidebar-item`}
                >
                  <ListItemIcon>
                    <GroupIcon fontSize='small' />
                  </ListItemIcon>
                  {isMenuOpen && <ListItemText primary={section.label} />}
                  {isMenuOpen &&
                    (isOpen ? (
                      <ExpandLess style={chevronStyle} fontSize='small' />
                    ) : (
                      <ExpandMore style={chevronStyle} fontSize='small' />
                    ))}
                </ListItem>
              );
              return (
                <React.Fragment key={section.key}>
                  {isMenuOpen ? (
                    groupHeader
                  ) : (
                    <Tooltip title={section.label} placement='right' arrow>
                      {groupHeader}
                    </Tooltip>
                  )}
                  <Collapse
                    in={isOpen}
                    timeout='auto'
                    unmountOnExit
                    style={{ marginLeft: isMenuOpen ? '10px' : 'unset' }}
                  >
                    {section.children.map((child) => {
                      const ChildIcon = child.icon;
                      const route = resolveRoute(child.route);
                      if (!route) return null;
                      return (
                        <ListItemComponent
                          key={child.key}
                          label={child.label}
                          route={route}
                          icon={<ChildIcon fontSize='small' />}
                          isMenuOpen={isMenuOpen}
                          isSelected={route === activeRoute}
                        />
                      );
                    })}
                  </Collapse>
                </React.Fragment>
              );
            })}

            {/* <ListItem button onClick={handleLogout} className='sidebar-item last-item'>
              <ListItemIcon>
                <ExitToAppIcon fontSize='small' />
              </ListItemIcon>
              <ListItemText primary='Logout' />
            </ListItem> */}
          </div>
        </Drawer>
        <div className='main-layout-toolbar-div'>
          <Toolbar className='header-title main-layout-toolbar'>
            <div className='main-toolbar-block main-toolbar-left d-flex align-items-center'>
              {window.innerWidth > 350 && <Breadcrumb />}
            </div>
            <div
              className='main-toolbar-block main-toolbar-center d-flex justify-center align-items-center'
              style={{ maxWidth: '250px' }}
            >
              {admin?.offices?.length > 1 && (
                <span className='table-select-filter header-office-select office-toolbar-center'>
                  <span className='f-16 fw-500 mr-4'>Office :</span>
                  <Select
                    className='mr-8 ml-4 f-16 office-toolbar-select'
                    // variant='standard'
                    value={admin?.selected_office}
                    onChange={changeOffice}
                  >
                    <MenuItem value={'all'} key={'all'}>
                      All Offices
                    </MenuItem>
                    {admin?.offices?.map((office) => (
                      <MenuItem value={office.client_office_id} key={office.client_office_id}>
                        {office.office_name}
                      </MenuItem>
                    ))}
                  </Select>
                </span>
              )}
            </div>
            <div className='main-toolbar-block main-toolbar-right d-flex flex-row justify-end align-items-center'>
              {/* {showResetFilters ? (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    startIcon={<Refresh />}
                    size='small'
                    // variant=''
                    onClick={handleResetAll}
                    className={'filter-reset-btn mr-16 f-14'}
                    sx={{ textTransform: 'none' }}
                  >
                    Reset Filters
                  </Button>
                </div>
              ) : (
                ''
              )} */}
              <div className='profile-chip'>
                {isClientAdmin && (
                  <>
                    <Chip
                      label={`Client : ${admin?.company_name}`}
                      variant='outlined'
                      className='mr-16'
                      color='primary'
                    />
                  </>
                )}
                {/* {isOfficeAdmin && (
                  <>
                    <Chip
                      label={`Client : ${admin?.company_name}`}
                      variant='outlined'
                      className='mr-16'
                      color='primary'
                    />
                    <Chip
                      label={`Office : ${
                        admin?.offices?.length > 1
                          ? 'All Offices'
                          : admin?.offices?.[0]?.office_name
                      }`}
                      variant='outlined'
                      className='mr-16'
                      color='primary'
                    />
                  </>
                )} */}
              </div>
              <div className='profile-chip user-name'>
                <Chip
                  onClick={handleDrawerOpen}
                  avatar={
                    <Avatar
                      sx={{ width: '34px', height: '34px', fontSize: '16px' }}
                      className='mr-4 cursor-pointer bg-p'
                    >
                      <Person style={{ color: 'white' }} />
                    </Avatar>
                  }
                  label={admin?.first_name + ' ' + admin?.last_name}
                  variant='outlined'
                  className='cursor-pointer'
                  color='primary'
                />
              </div>
            </div>
          </Toolbar>
          {showClosedOfficeNotice ? (
            <div className='main-layout-banner'>
              <Alert
                severity='warning'
                className='f-13 fw-500'
                onClose={dismissClosedOfficeNotice}
                closeText='Hide this notice'
              >
                {disableOffices?.join(', ')} office(s) is permanently closed. You can no longer
                create new orders or users for this office(s).
              </Alert>
            </div>
          ) : (
            ''
          )}

          <div
            style={{
              flexGrow: 1,
              overflowY: 'auto',
            }}
            id='main-scroll-container'
          >
            {children}
          </div>
        </div>
      </div>
      {/* <HelpFab /> */}
      <ProfileDrawer open={drawerOpen} onClose={handleDrawerClose} type='admin' />
    </>
  );
};;

export default MainLayout;
